import {
  OrderFlowSurface,
  RenderErrorSource,
  embedLoadSurfaceForPath,
  isOrderFlowLoadReported,
  reportPreScreenLoadFailure
} from './orderFlowLoad'

interface Candidate {
  surface: OrderFlowSurface
  source: RenderErrorSource
}

const isForeignScript = (filename: string): boolean => {
  if (!filename) return false
  try {
    return new URL(filename).origin !== window.location.origin
  } catch {
    return true
  }
}

const isNoiseError = ({ message, filename }: ErrorEvent): boolean =>
  message === 'Script error.' || message?.startsWith('ResizeObserver loop') || isForeignScript(filename)

const isCancellation = (reason: unknown): boolean => {
  const { code, name } = (reason ?? {}) as { code?: unknown; name?: unknown }
  return code === 'ERR_CANCELED' || name === 'AbortError' || name === 'CanceledError'
}

export const reportEmbedRenderCrash = (): void => {
  reportPreScreenLoadFailure({ failureReason: 'RENDER_ERROR', errorSource: 'boundary' })
}

/** A window error alone proves nothing; it is only reported if the page goes away before the first screen. */
export const installEmbedLoadErrorWatch = (target: Window = window): (() => void) => {
  let candidate: Candidate | null = null

  const record = (source: RenderErrorSource) => {
    const surface = embedLoadSurfaceForPath(window.location.pathname)
    if (!surface || isOrderFlowLoadReported(surface) || candidate?.surface === surface) return
    candidate = { surface, source }
  }

  const onError = (event: Event) => {
    if (!isNoiseError(event as ErrorEvent)) record('window_error')
  }
  const onRejection = (event: Event) => {
    if (!isCancellation((event as PromiseRejectionEvent).reason)) record('unhandled_rejection')
  }
  const onPageHide = (event: Event) => {
    // A page kept in the back/forward cache may be restored and still render its first screen.
    if ((event as PageTransitionEvent).persisted) return
    const pending = candidate
    candidate = null
    if (!pending) return
    if (embedLoadSurfaceForPath(window.location.pathname) !== pending.surface || isOrderFlowLoadReported(pending.surface)) return
    reportPreScreenLoadFailure({ failureReason: 'RENDER_ERROR', errorSource: pending.source })
  }

  target.addEventListener('error', onError)
  target.addEventListener('unhandledrejection', onRejection)
  target.addEventListener('pagehide', onPageHide)

  return () => {
    target.removeEventListener('error', onError)
    target.removeEventListener('unhandledrejection', onRejection)
    target.removeEventListener('pagehide', onPageHide)
    candidate = null
  }
}
